import { useState } from 'react';
import { Grid, Typography, Button } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useRequestPrice from '../hooks/api/useRequestPrice';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import Input from '../components/Input';
import Dropdown from '../components/Dropdown';
import { useTheme } from '@emotion/react';
import useWindowSize from '../hooks/useWindowResize';

const Price = () => {
	const { $t } = useIntl();
	const [selected, setSelected] = useState('yes');
	const theme = useTheme();
	const { width } = useWindowSize();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm({
		resolver: yupResolver(
			yup.object().shape({
				name: yup.string().required($t({ id: 'NameYup' })),
				phone: yup
					.string()
					.typeError($t({ id: 'PhoneYupNumber' }))
					.length(11, $t({ id: 'PhoneYupCh' }))
					.matches(/^[0-9]+$/, $t({ id: 'PhoneYupNumber' }))
					.required($t({ id: 'PhoneYup' })),
				email: yup
					.string()
					.email($t({ id: 'EmailYup' }))
					.required($t({ id: 'EmailYupIN' })),
				Product: yup.string().required($t({ id: 'ProductYup' })),
				code: yup.string().required($t({ id: 'CodeYup' })),
				tonnage: yup
					.number()
					.typeError($t({ id: 'TonnageYupNumber' }))
					.required($t({ id: 'TonnageYup' })),
				origin: yup.object().required($t({ id: 'OriginYup' })),
				destination: yup.object().required($t({ id: 'DestinationYup' })),
				road: yup.object().required($t({ id: 'RoadYup' })),
				grade: yup.object().required($t({ id: 'GradeYup' })),
				type: yup.object().required($t({ id: 'TypeYup' })),
			})
		),
		defaultValues: {
			name: '',
			phone: '',
			email: '',
			Product: '',
			code: '',
			tonnage: '',
			origin: null,
			destination: null,
			road: null,
			grade: null,
			type: null,
		},
	});

	const originOptions = [
		{ value: 1, label: $t({ id: 'TehranIranTitle' }) },
		{ value: 2, label: $t({ id: 'EsfehanIranTitle' }) },
		{ value: 3, label: $t({ id: 'TabrizIranTitle' }) },
	];
	const destinationOptions = [
		{ value: 1, label: $t({ id: 'TehranIranTitle' }) },
		{ value: 2, label: $t({ id: 'EsfehanIranTitle' }) },
		{ value: 3, label: $t({ id: 'TabrizIranTitle' }) },
	];
	const roadOptions = [
		{ value: 1, label: $t({ id: 'LevelOne' }) },
		{ value: 2, label: $t({ id: 'LevelTwo' }) },
		{ value: 3, label: $t({ id: 'LevelThree' }) },
	];
	const gradeOfRiskOptions = [
		{ value: 1, label: $t({ id: 'LevelOne' }) },
		{ value: 2, label: $t({ id: 'LevelTwo' }) },
		{ value: 3, label: $t({ id: 'LevelThree' }) },
	];
	const packageTypeOptions = [
		{ value: 1, label: $t({ id: 'Carton' }) },
		{ value: 2, label: $t({ id: 'CardBoard' }) },
		{ value: 3, label: $t({ id: 'Plastic' }) },
	];

	const requestPrice = useRequestPrice({
		onSuccess: data => {
			if (data.success) {
				toast.success('اطلاعات با موفقیت ارسال شد');
				reset();
			} else toast.error('مشکلی به وجود آمده است');
		},
		onError: error => {
			toast.error('مشکلی به وجود آمده است');
		},
	});

	const onSubmit = data => {
		const output = {
			name: data.name,
			phone: data.phone,
			email: data.email,
			Product: data.Product,
			code: data.code,
			tonnage: data.tonnage,
			origin: data.origin.label,
			destination: data.destination.label,
			road: data.road.label,
			grade: data.grade.label,
			type: data.type.label,
		};
		requestPrice.mutate(output);
	};

	return (
		<Grid
			bgcolor={'rgba(255, 251, 242, 1)'}
			py={{ xs: '18px', xxs: '27px', md: '75px', lg: '103px' }}
			px={'10%'}
		>
			<Grid className="card">
				<Grid className="title-box">
					<h1 className="title">
						{$t({ id: 'EnterInformationToInquireAboutThePrice' })}
					</h1>
				</Grid>
				<Grid
					display={'grid'}
					rowGap={'30px'}
					px={'7%'}
					py={'50px'}
				>
					<Grid
						display={'grid'}
						rowGap={'50px'}
						columnGap={'10%'}
						gridTemplateColumns={{
							xs: '1fr',
							sm: 'repeat(2, 1fr)',
							md: 'repeat(3, 1fr)',
						}}

					>
						<Input
							rules={{
								required: true,
							}}
							control={control}
							name="name"
							label={$t({ id: 'FirstAndLastNameInputLabel' })}
						/>
						<Input
							rules={{
								required: true,
								minLength: 11,
								maxLength: 11,
							}}
							control={control}
							name="phone"
							label={$t({ id: 'PhoneNumberInputLabel' })}
							type="number"
						/>
						<Input
							rules={{
								required: true,
							}}
							control={control}
							name="email"
							label={$t({ id: 'EmailInputLabel' })}
							type="email"
							error={errors?.name ? $t({ id: 'EmailYupIN' }) : ''}
						/>
						<Input
							rules={{
								required: true,
							}}
							control={control}
							name="Product"
							label={$t({ id: 'CargoNameInputLabel' })}
						/>
						<Input
							rules={{
								required: true,
							}}
							control={control}
							name="code"
							label={$t({ id: 'HsCodeInputLabel' })}
						/>
						<Input
							rules={{
								required: true,
							}}
							control={control}
							name="tonnage"
							type="number"
							label={$t({ id: 'CargoTonnageInputLabel' })}
						/>
						<Dropdown
							rules={{
								required: true,
							}}
							control={control}
							name="origin"
							label={$t({ id: 'OriginInputLabel' })}
							options={originOptions}
						/>
						<Dropdown
							rules={{
								required: true,
							}}
							control={control}
							name="destination"
							label={$t({ id: 'DestinationInputLabel' })}
							options={destinationOptions}
						/>
						<Dropdown
							rules={{
								required: true,
							}}
							control={control}
							name="road"
							label={$t({ id: 'MultiRouteInputLabel' })}
							options={roadOptions}
						/>
						<Dropdown
							rules={{
								required: true,
							}}
							control={control}
							name="grade"
							label={$t({ id: 'DegreeOfRiskOfTheGoodsInputLabel' })}
							options={gradeOfRiskOptions}
						/>
						<Dropdown
							rules={{
								required: true,
							}}
							control={control}
							name="type"
							label={$t({ id: 'PackageTypeInput' })}
							options={packageTypeOptions}
						/>
					</Grid>
					<Grid
						display={'flex'}
						alignItems={{ xs: 'start', sm: 'end' }}
						justifyContent={'space-between'}
						flexDirection={{ xs: 'column', sm: 'row' }}
						gap={'20px'}
					>
						<Grid
							fontSize={'16px'}
							display={'grid'}
							gap={'10px'}
						>
							<Typography
								variant={'span'}
								fontWeight={500}
							>
								{$t({ id: 'IsTheShipmentDeclared' })}
							</Typography>
							<Grid>
								<Grid
									display={'flex'}
									alignItems={'center'}
									gap={'40px'}
								>
									<Grid
										onClick={() => setSelected('yes')}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											cursor: 'pointer',
										}}
									>
										<Typography
											variant={'span'}
											fontWeight={500}
										>
											{$t({ id: 'Yes' })}
										</Typography>
										<Grid
											style={{
												width: '20px',
												height: '20px',
												backgroundColor:
													selected === 'yes'
														? 'rgba(255, 191, 63, 1)'
														: 'white',
												border: '1px solid rgba(255, 191, 63, 1)',
												borderRadius: '3px',
											}}
										></Grid>
									</Grid>
									<Grid
										onClick={() => setSelected('no')}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											cursor: 'pointer',
										}}
									>
										<Typography
											variant={'span'}
											fontWeight={500}
										>
											{$t({ id: 'No' })}
										</Typography>
										<Grid
											style={{
												width: '20px',
												height: '20px',
												backgroundColor:
													selected === 'no'
														? 'rgba(255, 191, 63, 1)'
														: 'white',
												border: '1px solid rgba(255, 191, 63, 1)',
												borderRadius: '3px',
											}}
										></Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Button
							onClick={handleSubmit(onSubmit, error => {
								//console.log(error);
							})}
							type={'submit'}
							variant={'contained'}
							style={{
								width: width > theme.breakpoints.values.sm ? '150px' : '100%',
							}}
							disabled={selected === 'no'}
						>
							<Typography
								variant={'subtitle1'}
								fontWeight={900}
							>
								{$t({ id: 'SendButtonTitle' })}
							</Typography>
						</Button>
					</Grid>
					<Typography
						fontSize={'16px'}
						fontWeight={500}
					>
						{$t({ id: 'AlertPricePage' })}
					</Typography>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default Price;
